import React, { useMemo, useState } from 'react';
import { ProviderPayAdyenProvider } from './provider/ProviderPayAdyenProvider';
import AdyenCheckout from '@adyen/adyen-web';
import { utilLog } from '../../../../util/utilLog';
import { AppCache } from '../../../../services/app-cache';
import { useZone } from '../../../../hooks/zone';
import { XLoader } from '../../../x/loader/XLoader';
import { UiOverlayLoader } from '../../../ui/UiOverlayLoader';
import styled from 'styled-components';
import { XGrid } from '@orderx/lab/ui';
import { apiProviderAdyenCheckoutCreatePaymentPost } from '../../../../api/provider.adyen.checkout.createPayment.[post]';
import { toastShowError } from '@orderx/lab/xr/toast';
import { apiProviderAdyenCheckoutGetAdditionalDetailsPost } from '../../../../api/provider.adyen.checkout.getAdditionalDetails.[post]';
import { XAlertType, XAlert } from '../../../x/alert/XAlert';
import { isMobile } from '../../../../util/isMobile';

const Wrapper = styled(XGrid)`
    position: relative;
`;

interface Props {
    config: {
        amount: number;
        checkoutConfig: any;
        paymentMethodsConfiguration: any;
        orderId?: number;
        paymentId?: number;
        takeawayAt?: string;
    };

    onSuccess: any;
}

let _takeawayAt;
let _browserInfo;

export function ProviderPayAdyen({ config, onSuccess }: Props) {
    const { zoneId } = useZone();
    const { amount, checkoutConfig, paymentMethodsConfiguration, orderId, paymentId, takeawayAt } = config;
    const [isLoading, setIsLoading] = useState(false);
    const [isSubmitted, setIsSubmitted] = useState(false);
    const [metadata, setMetadata] = useState<{
        browserInfo?: any;
    }>();

    _takeawayAt = takeawayAt;

    const checkout = useMemo(() => {
        const meta = {
            ...metadata,
            amount,
            orderId,
            paymentId,
            hostname: window.location.hostname,
            origin: window.location.origin,
        };

        async function handleSubmit({ state, dropin }) {
            setIsLoading(true);

            try {
                const response = await apiProviderAdyenCheckoutCreatePaymentPost({
                    body: {
                        zoneId,
                        meta: {
                            ...meta,
                            browserInfo: _browserInfo || metadata?.browserInfo,
                        },
                        paymentMethod: state.data.paymentMethod,
                        takeawayAt: _takeawayAt,
                        isDesktop: !isMobile(),
                    },
                });

                setIsSubmitted(true);

                const { data } = response;

                const types = {
                    SUCCESS() {
                        onSuccess(response);
                    },
                    ERROR() {
                        toastShowError('Something went wrong');
                    },
                    REDIRECT() {
                        AppCache.set('checkoutResponse', response);

                        if (data?.action?.url) {
                            checkout.createFromAction(data.action).mount('#adyen-checkout-container');
                        } else {
                            dropin.handleAction(data.action);
                        }
                    },
                };

                const method = types[response.type];

                if (method) {
                    await method();
                } else if (response.data.action) {
                    if (data.action?.type === 'redirect') {
                        utilLog(data.url);
                    } else {
                    }

                    dropin.handleAction(data.action);
                } else {
                    await types.SUCCESS();
                }
            } catch (error) {
                utilLog('onSubmit catch error', error);
                toastShowError('Failed to pay');
            }

            setIsLoading(false);
        }

        async function handleAdditionalDetails({ state, dropin }) {
            utilLog('onAdditionalDetails', { state });
            setIsLoading(true);

            try {
                const response = await apiProviderAdyenCheckoutGetAdditionalDetailsPost({
                    body: {
                        zoneId,
                        meta: {
                            ...meta,
                            browserInfo: _browserInfo || metadata?.browserInfo,
                        },
                        paymentMethod: state.data.paymentMethod,
                        takeawayAt: _takeawayAt,
                    },
                });

                if (response.action) {
                    dropin.handleAction(response.action);
                } else {
                    onSuccess(response);
                }
            } catch (error) {
                setIsSubmitted(false);
                utilLog('onAdditionalDetails catch error', error);
                toastShowError('Failed to pay');
            }

            setIsLoading(false);
        }

        return new AdyenCheckout({
            ...checkoutConfig,
            onChange(state) {
                setMetadata(state?.data);
                _browserInfo = state?.data?.browserInfo;
            },
            onSubmit(state, dropin) {
                return handleSubmit({ state, dropin });
            },
            onAdditionalDetails(state, dropin) {
                return handleAdditionalDetails({ state, dropin });
            },
        });
    }, [checkoutConfig]);

    if (!checkout) {
        return <XLoader />;
    }

    return (
        <Wrapper>
            {isLoading && <UiOverlayLoader />}

            {!isLoading && isSubmitted && <XAlert type={XAlertType.SUCCESS}>Payment started</XAlert>}

            {!isSubmitted &&
                checkoutConfig.paymentMethodsResponse.paymentMethods.map((paymentMethod) => (
                    <ProviderPayAdyenProvider
                        checkout={checkout}
                        type={paymentMethod.type}
                        configuration={{
                            ...paymentMethod.configuration,
                            ...paymentMethodsConfiguration[paymentMethod.type],
                        }}
                        onSuccess={onSuccess}
                        key={paymentMethod.type}
                    />
                ))}
        </Wrapper>
    );
}
